import React, { useState, useMemo } from 'react';
import { useInventoryPlanning } from '../../contexts/InventoryPlanningContext';
import MonthlyPivotTable from '../MonthlyPivotTable';
import SelectSupplierLocationModal from '../SelectSupplierLocationModal';
import ArticleIdCell from '../ArticleIdCell';
import { Download } from 'lucide-react';
import { pivotSalesToCSV } from '../../utils/csvExport';
import { formatIntegerWithColor } from '../../utils/numberFormatting';

const SalesHistoryTab: React.FC = () => {
  const { data, selectedArticleIds, ensureSections, sectionStates } = useInventoryPlanning();
  React.useEffect(() => {
    ensureSections(['sales']);
  }, [ensureSections]);
  const sectionState = sectionStates.sales;
  const allRows = useMemo(() => data?.sales_history || [], [data?.sales_history]);
  
  // Filter sales history by selected articles
  const rows = useMemo(() => {
    if (!selectedArticleIds || selectedArticleIds.length === 0) {
      return allRows; // Export all if no selection
    }
    return allRows.filter((row: any) => 
      selectedArticleIds.includes(String(row.ref || row.article_id))
    );
  }, [allRows, selectedArticleIds]);
  
  const [modalArticle, setModalArticle] = useState<string | null>(null);

  const handleExportSales = () => {
    const csvContent = pivotSalesToCSV(rows, 'ref', 'date', 'quantity');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ventes_${new Date().toISOString().slice(0, 10)}.csv`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white shadow rounded p-4" style={{ minWidth: '600px' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-medium">Ventes passées</div>
        <button
          onClick={handleExportSales}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
          title="Exporter toutes les ventes passées"
        >
          <Download size={14} />
          Exporter Ventes
        </button>
      </div>
      {sectionState?.loading && <div className="text-xs text-gray-500 mb-2">Chargement des ventes…</div>}
      {sectionState?.error && <div className="text-xs text-red-600 mb-2">{sectionState.error}</div>}
      <div className="text-xs text-gray-500 mb-2">{rows.length} entrées</div>
      <MonthlyPivotTable
        rows={rows}
        idField="ref"
        dateField="date"
        valueField="quantity"
        idHeader="Article"
        actionHeader=""
        onRowClick={(id) => setModalArticle(String(id))}
        renderIdCell={(id) => <ArticleIdCell id={id} onClick={() => setModalArticle(id)} />}
        valueFormatter={(v) => formatIntegerWithColor(v)}
      />
      <SelectSupplierLocationModal open={!!modalArticle} articleId={modalArticle} onClose={() => setModalArticle(null)} />
    </div>
  );
};

export default SalesHistoryTab;


